import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Card } from "../../components/UI";
import { LineChart, Sparkles, ArrowRight, Loader2, Tv, HandCoins, Megaphone } from "lucide-react";

export default function SimplePerformance() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/war-room/overview")
      .then((r) => setOverview(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;

  const adv = overview?.advertising || {};
  const fr = overview?.fundraising || {};
  const attr = overview?.attribution || {};

  const lines = [
    adv.total_campaigns
      ? `You have ${adv.total_campaigns} campaign${adv.total_campaigns === 1 ? "" : "s"} with a combined approved budget of $${Number(adv.total_budget || 0).toLocaleString()} — ${adv.active_campaigns || 0} currently active.`
      : "You haven't created any advertising campaigns yet.",
    fr.total_raised
      ? `Your fundraisers have raised $${Number(fr.total_raised).toLocaleString()} from ${fr.contributions || 0} contribution${fr.contributions === 1 ? "" : "s"} across ${fr.donors || 0} donor${fr.donors === 1 ? "" : "s"}.`
      : "No contributions recorded yet — create a donation page to start raising.",
    attr.ad_attributed_amount
      ? `$${Number(attr.ad_attributed_amount).toLocaleString()} of donations came directly from your advertising and shared links.`
      : null,
    attr.microsite_visits
      ? `Your campaign microsites have been visited ${Number(attr.microsite_visits).toLocaleString()} time${attr.microsite_visits === 1 ? "" : "s"}.`
      : null,
  ].filter(Boolean);

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto" data-testid="simple-performance">
      <div className="text-[11px] uppercase tracking-widest text-cyan-300 font-semibold mb-2">Performance</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold mb-6">How your campaign is doing</h1>

      <div className="grid sm:grid-cols-4 gap-4 mb-6" data-testid="performance-stats">
        <Stat icon={Megaphone} label="Ad budget" value={`$${Number(adv.total_budget || 0).toLocaleString()}`} />
        <Stat icon={Tv} label="Active campaigns" value={`${adv.active_campaigns || 0}`} />
        <Stat icon={HandCoins} label="Funds raised" value={`$${Number(fr.total_raised || 0).toLocaleString()}`} />
        <Stat icon={LineChart} label="Donors" value={`${fr.donors || 0}`} />
      </div>

      <Card className="mb-6" data-testid="performance-plain-english">
        <h3 className="font-display text-lg font-bold mb-3">In plain English</h3>
        <ul className="space-y-2.5">
          {lines.map((l, i) => (
            <li key={i} className="text-sm text-slate-300 leading-relaxed flex gap-2.5" data-testid={`performance-line-${i}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-1.5 shrink-0" /> {l}
            </li>
          ))}
        </ul>
        <button onClick={() => navigate("/ask-totalreach")} data-testid="performance-ask-ai"
          className="mt-5 inline-flex items-center gap-2 bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold rounded-full px-5 py-2.5 text-sm hover:opacity-90">
          <Sparkles className="w-4 h-4" /> Ask TotalReach about these results
        </button>
      </Card>

      <div className="grid sm:grid-cols-3 gap-3" data-testid="performance-pro-links">
        <ProLink label="Detailed advertising reports" to="/reports" onClick={navigate} testid="performance-link-reports" />
        <ProLink label="CTV reports" to="/reports/ctv" onClick={navigate} testid="performance-link-ctv" />
        <ProLink label="Fundraising dashboard" to="/fundraising" onClick={navigate} testid="performance-link-fundraising" />
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
    <Icon className="w-4 h-4 text-cyan-300 mb-2" />
    <div className="font-display text-xl font-bold">{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{label}</div>
  </div>
);

const ProLink = ({ label, to, onClick, testid }) => (
  <button onClick={() => onClick(to)} data-testid={testid}
    className="group text-left rounded-xl border border-white/10 bg-white/[0.02] hover:border-cyan-400/30 transition-colors p-4 flex items-center justify-between gap-2">
    <span className="text-sm">{label}</span>
    <ArrowRight className="w-3.5 h-3.5 text-slate-600 group-hover:text-cyan-300 transition-colors shrink-0" />
  </button>
);
